"use client";

import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { SIGNIFICANT_FIGURES_CALCULATOR_STRINGS } from "@/components/tools/significant-figures-calculator";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import { toSuperscript } from "@/lib/tool-kit/math/primes";
import {
  canWritePlain,
  type DigitInfo,
  exactOf,
  explainRound,
  formatENotation,
  formatPlain,
  formatScientific,
  formatUnrounded,
  parseSigFigNumber,
  type RoundingMode,
  roundToSigFigs,
  type SigFigNumber,
  type SigOperation,
  type SigValue,
  sigArithmetic,
  sigCount,
  sigValueOf,
} from "@/lib/tool-kit/math/sig-figs";

/*
 * The three result panels of the significant-figures calculator: count, round and
 * calculate. Split from the widget to keep each file readable; every visible word still
 * comes from the widget's `SIGNIFICANT_FIGURES_CALCULATOR_STRINGS`, passed in as
 * `strings`.
 */

type Strings = typeof SIGNIFICANT_FIGURES_CALCULATOR_STRINGS;

const SYMBOL: Record<SigOperation, string> = {
  add: "+",
  subtract: "−",
  multiply: "×",
  divide: "÷",
};

function placeName(place: number, strings: Strings): string {
  const named = (strings.places as Record<string, string>)[String(place)];
  return named ?? t(strings.placeOther, { power: toSuperscript(place) });
}

/** The plain form when it can be written, else scientific. */
function display(value: SigValue, mark: string): string {
  return canWritePlain(value)
    ? formatPlain(value, mark).text
    : formatScientific(value, mark);
}

/** "1200." or "1.200 × 10³": the ways to write every digit of an ambiguous number. */
function fullReadingForms(n: SigFigNumber, strings: Strings, mark: string) {
  const first = n.digits.findIndex((d) => d.kind !== "leading");
  const digits = n.digits.slice(first);
  const all: SigValue = {
    negative: n.negative,
    coefficient: BigInt(digits.map((d) => d.digit).join("")),
    lastPlace: digits.at(-1)?.place ?? 0,
  };
  const scientific = formatScientific(all, mark);
  const plain = canWritePlain(all) ? formatPlain(all, mark) : null;
  return plain?.trailingPoint
    ? t(strings.orJoin, { first: plain.text, second: scientific })
    : scientific;
}

function useNumberParts() {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const count = (template: string, n: number) =>
    tPlural(template, locale, n, { count: fmt(n) }).trim();
  return { mark, count, fmt };
}

function ParseProblem({
  text,
  empty,
  strings,
}: {
  text: string;
  empty: string;
  strings: Strings;
}) {
  const parsed = parseSigFigNumber(text);
  if (!("error" in parsed) || parsed.error === "empty") {
    return <Notice>{empty}</Notice>;
  }
  return (
    <ErrorNotice>
      {parsed.error === "too-long" ? strings.tooLong : strings.invalid}
    </ErrorNotice>
  );
}

const CHIP: Record<string, string> = {
  significant: "border-primary/40 bg-primary/10 text-foreground",
  not: "border-border bg-card text-muted-foreground opacity-70",
  ambiguous:
    "border-dashed border-muted-foreground bg-card text-muted-foreground",
};

function chipStyle(d: DigitInfo): string {
  if (d.kind === "trailing-ambiguous") {
    return CHIP.ambiguous;
  }
  return d.significant ? CHIP.significant : CHIP.not;
}

function DigitChips({ n, strings }: { n: SigFigNumber; strings: Strings }) {
  const point = n.mantissa.indexOf(".");
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-1 font-mono text-lg">
        {n.negative ? <span className="px-1">−</span> : null}
        {n.digits.map((d, index) => (
          // Digits are positional: the same digit can appear twice.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional digits
          <span className="flex items-end gap-1" key={index}>
            {point === index ? <span>.</span> : null}
            <span className={`rounded-md border px-2 py-1 ${chipStyle(d)}`}>
              {d.digit}
            </span>
          </span>
        ))}
        {point === n.mantissa.length - 1 ? <span>.</span> : null}
        {n.scientific ? (
          <span className="px-1">× 10{toSuperscript(n.exponent)}</span>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-3 text-muted-foreground text-xs">
        <span className={`rounded-md border px-2 py-0.5 ${CHIP.significant}`}>
          {strings.legendSignificant}
        </span>
        <span className={`rounded-md border px-2 py-0.5 ${CHIP.not}`}>
          {strings.legendNot}
        </span>
        <span className={`rounded-md border px-2 py-0.5 ${CHIP.ambiguous}`}>
          {strings.legendAmbiguous}
        </span>
      </div>
    </div>
  );
}

export function CountPanel({
  text,
  strings,
}: {
  text: string;
  strings: Strings;
}) {
  const { mark, count } = useNumberParts();
  const n = parseSigFigNumber(text);
  if ("error" in n) {
    return (
      <ParseProblem empty={strings.enterCount} strings={strings} text={text} />
    );
  }
  const value = sigValueOf(n);
  const verdict = (d: DigitInfo) => {
    if (d.kind === "trailing-ambiguous") {
      return strings.maybe;
    }
    return d.significant ? strings.yes : strings.no;
  };
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.sigFigs} value={n.sigFigs} />
        <StatCard label={strings.decimalPlaces} value={n.decimalPlaces} />
        <StatCard
          label={strings.scientific}
          value={n.isZero ? "0" : formatScientific(value, mark)}
        />
      </StatGrid>
      {n.commaAmbiguity ? (
        <Notice>
          {t(strings.commaNote, { typed: text.trim(), read: n.mantissa })}
        </Notice>
      ) : null}
      {n.ambiguousZeros > 0 ? (
        <Notice>
          {t(strings.ambiguousNote, {
            number: text.trim(),
            count: count(strings.sigFigsCount, n.sigFigs),
            zeros: count(strings.trailingZeros, n.ambiguousZeros),
            forms: fullReadingForms(n, strings, mark),
          })}
        </Notice>
      ) : null}
      {n.isZero ? (
        <Notice>
          {t(strings.zeroNote, {
            places: count(strings.placesCount, n.decimalPlaces),
          })}
        </Notice>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.digitByDigit}</h3>
      <DigitChips n={n} strings={strings} />
      <DataTable
        head={[
          strings.digitColumn,
          strings.placeColumn,
          strings.significantColumn,
          strings.whyColumn,
        ]}
        rows={n.digits.map((d) => [
          d.digit,
          placeName(d.place, strings),
          verdict(d),
          strings.reasons[d.kind],
        ])}
      />
    </>
  );
}

export function RoundPanel({
  text,
  target,
  rule,
  strings,
}: {
  text: string;
  target: number;
  rule: RoundingMode;
  strings: Strings;
}) {
  const { mark, count, fmt } = useNumberParts();
  const n = parseSigFigNumber(text);
  if ("error" in n) {
    return (
      <ParseProblem empty={strings.enterRound} strings={strings} text={text} />
    );
  }
  const exact = exactOf(n);
  const result = roundToSigFigs(exact, target, rule);
  const why = explainRound(exact, target, rule);
  const plain = canWritePlain(result) ? formatPlain(result, mark) : null;
  const scientific = formatScientific(result, mark);
  const shown = plain?.text ?? scientific;
  const halfway =
    rule === "half-even" && why.decider === "5" && !why.restNonzero;
  const direction = why.roundedUp ? strings.up : strings.down;
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.rounded} value={shown} />
        <StatCard label={strings.scientific} value={scientific} />
        <StatCard label={strings.eNotation} value={formatENotation(result)} />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={shown}
      />
      {plain === null ? <Notice>{strings.plainTooLong}</Notice> : null}
      {plain?.trailingPoint ? (
        <Notice>{t(strings.trailingPointNote, { text: plain.text })}</Notice>
      ) : null}
      {plain?.ambiguous ? (
        <Notice>
          {t(strings.ambiguousResultNote, {
            text: plain.text,
            scientific,
            n: fmt(target),
          })}
        </Notice>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.howRounded}</h3>
      <ol className="list-decimal space-y-1 pl-5 text-muted-foreground text-sm leading-relaxed">
        {why.padded > 0 ? (
          <li>
            {t(strings.stepPad, {
              number: text.trim(),
              have: count(strings.sigFigsCount, target - why.padded),
              padding: count(strings.paddingZeros, why.padded),
              n: fmt(target),
            })}
          </li>
        ) : (
          <>
            <li>{t(strings.stepCount, { n: fmt(target), kept: why.kept })}</li>
            <li>
              {halfway
                ? t(strings.stepHalfEven, { direction })
                : t(strings.stepDecider, { digit: why.decider, direction })}
            </li>
          </>
        )}
        {why.carried ? (
          <li>{t(strings.stepCarry, { result: shown })}</li>
        ) : null}
        <li>{t(strings.stepResult, { result: shown })}</li>
      </ol>
    </>
  );
}

function OperandLine({
  text,
  exact,
  strings,
}: {
  text: string;
  exact: boolean;
  strings: Strings;
}) {
  const { count } = useNumberParts();
  const n = parseSigFigNumber(text);
  if ("error" in n) {
    return null;
  }
  if (exact) {
    return <p>{t(strings.operandExact, { number: text.trim() })}</p>;
  }
  const place = placeName(n.lastPlace, strings);
  if (n.isZero) {
    return <p>{t(strings.operandZero, { number: text.trim(), place })}</p>;
  }
  return (
    <p>
      {t(strings.operandMeasured, {
        number: text.trim(),
        count: count(strings.sigFigsCount, n.sigFigs),
        place,
      })}
    </p>
  );
}

export function CalculatePanel({
  a,
  b,
  exactA,
  exactB,
  operation,
  rule,
  strings,
}: {
  a: string;
  b: string;
  exactA: boolean;
  exactB: boolean;
  operation: SigOperation;
  rule: RoundingMode;
  strings: Strings;
}) {
  const { mark, count } = useNumberParts();
  const first = parseSigFigNumber(a);
  const second = parseSigFigNumber(b);
  if ("error" in first || "error" in second) {
    return (
      <ParseProblem
        empty={strings.enterTwo}
        strings={strings}
        text={"error" in first ? a : b}
      />
    );
  }
  const result = sigArithmetic(
    { number: first, exact: exactA },
    { number: second, exact: exactB },
    operation,
    rule
  );
  if (result === null) {
    return <ErrorNotice>{strings.divideByZero}</ErrorNotice>;
  }
  const unrounded = formatUnrounded(result, rule, mark);
  const answer =
    result.rule === "exact" ? unrounded : display(result.result, mark);
  const ruleText = {
    "sig-figs": t(strings.ruleMultiply, {
      count: count(strings.sigFigsCount, result.limitingSigFigs ?? 0),
    }),
    precision: t(strings.ruleAdd, {
      place: placeName(result.limitingPlace ?? 0, strings),
    }),
    exact: strings.ruleExact,
    zero: strings.ruleZero,
  }[result.rule];
  const ambiguous = [
    { text: a, n: first, exact: exactA },
    { text: b, n: second, exact: exactB },
  ].filter((o) => !o.exact && o.n.ambiguousZeros > 0);
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.answer} value={answer} />
        <StatCard label={strings.unrounded} value={unrounded} />
        <StatCard
          label={strings.sigFigsInAnswer}
          value={result.rule === "exact" ? "∞" : sigCount(result.result)}
        />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={answer}
      />
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 text-muted-foreground text-sm leading-relaxed">
        <p className="font-medium text-foreground">{strings.working}</p>
        <OperandLine exact={exactA} strings={strings} text={a} />
        <OperandLine exact={exactB} strings={strings} text={b} />
        <p>{ruleText}</p>
        <p className="font-mono">
          {t(strings.unroundedLine, {
            expression: `${a.trim()} ${SYMBOL[operation]} ${b.trim()}`,
            value: unrounded,
          })}
        </p>
        {result.rule === "exact" ? null : (
          <p className="font-mono text-foreground">
            {t(strings.roundedLine, { value: answer })}
          </p>
        )}
      </div>
      {result.unroundedApproximate ? (
        <Notice>{strings.repeatingNote}</Notice>
      ) : null}
      {ambiguous.map((o) => (
        <Notice key={o.text}>
          {t(strings.ambiguousOperand, {
            number: o.text.trim(),
            count: count(strings.sigFigsCount, o.n.sigFigs),
            forms: fullReadingForms(o.n, strings, mark),
          })}
        </Notice>
      ))}
      <p className="text-muted-foreground text-xs">{strings.roundOnceTip}</p>
    </>
  );
}
